// Reads Rapier collision events and filters them into qualifying hits.
//
// Three gates, in order:
//  1. Qualifying attacker bone — hand during swing 'follow', foot during
//     jump-kick, head during headbutt. Everything else ignored.
//  2. Force gate — attacker bone linear speed ≥ hitForceSpeedMin, AND the
//     velocity component pointing AT the victim ≥ hitForceSpeedMin × 0.6
//     (so a fast bone swinging PAST a victim doesn't count).
//  3. Accuracy ∈ [0,1] — weighted sum of (velocity-alignment, speed vs ideal,
//     attacker-facing). Below accuracyFloor: rejected.
// A passing hit calls Character.takeImpact with (strength, accuracy,
// attackerBone, victimBone). HP/KO accounting lives on Character.

import RAPIER from '@dimforge/rapier3d-compat';
import { CONFIG } from '../config';
import { Bone } from './Ragdoll';
import type { Character } from './Character';
import type { ImpactEvent } from './SimState';

interface CharacterRefs {
  ch: Character;
  bodyToBone: Map<number, Bone>; // RigidBody.handle → Bone
}

const C = CONFIG.character;

export class Combat {
  private chars: CharacterRefs[] = [];
  // Latest registered hit — for debug monitors.
  lastHitAccuracy = 0;
  lastHitStrength = 0;
  lastHitAttackerBone: Bone | -1 = -1;

  registerCharacter(ch: Character): void {
    const bodyToBone = new Map<number, Bone>();
    for (let i = 0; i < ch.ragdoll.handles.bodies.length; i++) {
      bodyToBone.set(ch.ragdoll.handles.bodies[i]!.handle, i as Bone);
    }
    this.chars.push({ ch, bodyToBone });
  }

  reset(): void { /* no-op for now; chars are reused */ }

  process(world: RAPIER.World, queue: RAPIER.EventQueue, out: ImpactEvent[]): void {
    queue.drainCollisionEvents((h1, h2, started) => {
      if (!started) return;
      const c1 = world.getCollider(h1);
      const c2 = world.getCollider(h2);
      if (!c1 || !c2) return;
      const b1 = c1.parent();
      const b2 = c2.parent();
      if (!b1 || !b2) return;

      const owner1 = this.findOwner(b1.handle);
      const owner2 = this.findOwner(b2.handle);
      if (!owner1 || !owner2) return;
      if (owner1.ch === owner2.ch) return; // self-collision (shouldn't happen w/ groups)

      // Try owner1 attacking owner2, and vice-versa.
      this.tryHit(owner1, b1, owner2, b2, out);
      this.tryHit(owner2, b2, owner1, b1, out);
    });
  }

  private tryHit(att: CharacterRefs, attBody: RAPIER.RigidBody, vic: CharacterRefs, vicBody: RAPIER.RigidBody, out: ImpactEvent[]): void {
    const attBone = att.bodyToBone.get(attBody.handle);
    if (attBone === undefined) return;

    // ---- GATE 1: qualifying attacker bone + live move state ----
    if (!isQualifyingAttacker(attBone, att.ch)) return;
    // Defensive: a stunned attacker whose swing state got frozen mid-'follow'
    // could still let a floppy arm register. Require non-stunned attacker.
    if (att.ch.stunRemaining > 0 || att.ch.knockedOut) return;

    // ---- GATE 2: force threshold ----
    const v = attBody.linvel();
    const ap = attBody.translation();
    const vp = vicBody.translation();
    const dx = vp.x - ap.x, dy = vp.y - ap.y, dz = vp.z - ap.z;
    const dLen = Math.hypot(dx, dy, dz) || 1e-3;
    // Scale read-out velocities to compensate for solver-version differences
    // (see CONFIG.character.hitForceVelocityScale). 1.0 = legacy 0.14 numbers.
    const velScale = C.hitForceVelocityScale;
    const speed = Math.hypot(v.x, v.y, v.z) * velScale;
    if (speed < C.hitForceSpeedMin) return;
    const towardSpeed = ((v.x * dx + v.y * dy + v.z * dz) / dLen) * velScale;
    if (towardSpeed < C.hitForceSpeedMin * 0.6) return;

    // ---- GATE 3: accuracy ----
    const align = clamp01(towardSpeed / speed);
    if (align < C.hitVelAlignMin) return;
    const speedQ = clamp01(speed / C.hitIdealSpeed);
    // Facing: attacker's forward (from targetYaw) vs XZ dir-to-victim.
    const fwdX = Math.sin(att.ch.yaw);
    const fwdZ = Math.cos(att.ch.yaw);
    const dxz = Math.hypot(dx, dz) || 1e-3;
    const facing = clamp01((fwdX * dx + fwdZ * dz) / dxz);
    const accuracy = clamp01(
      C.accuracyAlignWeight * align +
      C.accuracySpeedWeight * speedQ +
      C.accuracyFacingWeight * facing
    );
    if (accuracy < C.accuracyFloor) return;

    // ---- Strength (drives FX intensity + victim knockback) ----
    const strength = Math.min(
      C.hitStrengthMax,
      (speed - C.hitStrengthSpeedOffset) * C.hitStrengthSpeedK
    );
    if (strength < C.hitStrengthMin) return;

    const vicBone = vic.bodyToBone.get(vicBody.handle) ?? Bone.Torso;
    const t = attBody.translation();
    vic.ch.takeImpact(strength, accuracy, attBone, vicBone, t.x, t.z);
    // Drop the event if the attacker pose is non-finite. See the matching
    // guard on Character footstep/swing pushes — bad coords flow straight
    // into PositionalAudio.position and wedge the render loop.
    if (Number.isFinite(t.x) && Number.isFinite(t.y) && Number.isFinite(t.z)) {
      out.push({
        victimId: vic.ch.id,
        attackerId: att.ch.id,
        attackerBone: attBone,
        x: t.x, y: t.y, z: t.z,
        strength,
        accuracy
      });
    }
    this.lastHitAccuracy = accuracy;
    this.lastHitStrength = strength;
    this.lastHitAttackerBone = attBone;
  }

  private findOwner(bodyHandle: number): CharacterRefs | undefined {
    for (const c of this.chars) if (c.bodyToBone.has(bodyHandle)) return c;
    return undefined;
  }
}

function isQualifyingAttacker(bone: Bone, ch: Character): boolean {
  if (bone === Bone.LowerArmL) return ch.swingL.phase === 'follow';
  if (bone === Bone.LowerArmR) return ch.swingR.phase === 'follow';
  if (bone === Bone.LowerLegL || bone === Bone.LowerLegR) return ch.jumpKickActive > 0;
  if (bone === Bone.Head) return ch.headbuttActive > 0;
  return false;
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
